/**
 * Trade Audio — sound functions for order fills.
 *
 * Three tiers:
 * - playFillTone()  — standard fill (800Hz, 0.2s)
 * - playWhaleTone() — large fill 1000+ contracts (500Hz double-ping)
 * - playTickTone()  — soft tick for "all trades" mode (1200Hz click)
 */

import { playTone } from './audioContext';
import { readSoundSettings } from './soundSettings';
import type { KalshiFill } from './kalshiApi';

/** Standard fill — 800Hz sine, 0.2s */
export function playFillTone(): void {
  playTone(800, 0.2, 0.3);
}

/** Whale fill (1000+ contracts) — 500Hz double-ping, louder */
export function playWhaleTone(): void {
  playTone(500, 0.15, 0.4);
  setTimeout(() => playTone(500, 0.15, 0.4), 200);
}

/** Soft tick for all-trades mode — 1200Hz click */
export function playTickTone(): void {
  playTone(1200, 0.05, 0.15);
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Replay fill sounds sequentially from an array of fills.
 * Last fill always plays as whale so both tiers are tested.
 * Checks sound settings before each tone — bails if user toggles off mid-replay.
 */
export async function replayFillSounds(fills: KalshiFill[]): Promise<void> {
  for (let i = 0; i < fills.length; i++) {
    const settings = readSoundSettings();
    if (!settings.tradeSoundEnabled) return;

    const isLast = i === fills.length - 1;
    if (isLast) {
      playWhaleTone();
    } else {
      playFillTone();
    }

    if (i < fills.length - 1) {
      await sleep(800);
    }
  }
}
